import { useState, useEffect } from "react";
import { Box, CircularProgress, Typography, useTheme, Divider, Card } from "@mui/material";
import { tokens } from "../../theme";
import TokenIcon from "@mui/icons-material/Token";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import axios from "axios";

const DecTokenStats = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tokenData, setTokenData] = useState({
    total_supply: 0,
    circulating_supply: 0,
    burned: 0,
    splinterlands_balance: 0,
    dao_balance: 0,
    max_supply: 725000000000 // Maximum DEC supply
  });

  // Function to format large numbers
  const formatNumber = (value) => {
    if (!value && value !== 0) return '0';
    
    // Format to millions or billions with suffix
    if (value >= 1000000000) {
      return `${(value / 1000000000).toFixed(2)}B`;
    } else if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else {
      return value.toLocaleString();
    }
  };

  // Fetch DEC token data from new DEC data API
  const fetchTokenData = async () => {
    try {
      setLoading(true);
      
      // Fetch from DEC data endpoint ONLY
      const decDataResponse = await axios.get("https://ecapi.splinterlands.com/players/dec_data");

      if (decDataResponse.data) {
        const decData = decDataResponse.data.supply;

        // Use data from DEC API
        const totalSupply = Number(decData.total_supply) || 0;
        const circulatingSupply = Number(decData.circulating) || 0;
        const burned = Number(decData.burned?.total) || 0;

        // Company balance from market escrow
        const splinterlandsBalance = Number(decData.markets_escrow?.['$SL-MARKET']) || 0;

        // Get DAO balance from reserves.total (includes all DAO reserves)
        const daoBalance = Number(decData.reserves?.['total']) || 0;

        setTokenData({
          total_supply: totalSupply,
          circulating_supply: circulatingSupply,
          burned,
          splinterlands_balance: splinterlandsBalance,
          dao_balance: daoBalance,
          max_supply: totalSupply // Use actual total supply instead of hardcoded max
        });
        setError(null);
      }
    } catch (err) {
      console.error("Failed to fetch DEC token data:", err);
      setError("Failed to load DEC token data");
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchTokenData();

    // Set up interval to refresh data every minute
    const intervalId = setInterval(fetchTokenData, 60000);

    return () => clearInterval(intervalId);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      height="100%"
    >
      {/* Total Supply Card */}
      <Card 
        sx={{ 
          backgroundColor: colors.primary[400], 
          p: 2,
          borderRadius: 2,
          border: `1px solid ${colors.greenAccent[600]}`,
        }}
      >
        <Typography variant="body2" color={colors.grey[300]} sx={{ mb: 0.5 }}>
          Total Supply
        </Typography>
        <Typography 
          variant="h2" 
          sx={{ 
            fontWeight: "bold",
            color: colors.greenAccent[400]
          }}
        >
          {formatNumber(tokenData.total_supply)}
        </Typography>
      </Card>

      {/* DAO Holdings Card */}
      <Card 
        sx={{ 
          backgroundColor: colors.primary[400], 
          p: 2,
          borderRadius: 2,
          border: `1px solid rgba(232, 168, 56, 0.5)`,
        }}
      >
        <Box display="flex" alignItems="center" mb={0.5}>
          <AccountBalanceIcon sx={{ color: '#e8a838', fontSize: 20, mr: 1 }} />
          <Typography variant="body2" color="#e8a838" fontWeight="bold">
            DAO Holdings
          </Typography>
        </Box>
        <Typography 
          variant="h2" 
          sx={{ 
            fontWeight: "bold",
            color: '#e8a838'
          }}
        >
          {formatNumber(tokenData.dao_balance)}
        </Typography>
        <Typography variant="body2" color={colors.grey[300]}>
          {((tokenData.dao_balance / tokenData.total_supply) * 100).toFixed(1)}% of total supply
        </Typography>
      </Card>

      {/* Total Burned Card */}
      <Card 
        sx={{ 
          backgroundColor: colors.primary[400], 
          p: 2,
          borderRadius: 2,
          border: `1px solid ${colors.redAccent[600]}`,
        }}
      >
        <Box display="flex" alignItems="center" mb={0.5}>
          <LocalFireDepartmentIcon sx={{ color: colors.redAccent[600], fontSize: 20, mr: 1 }} />
          <Typography variant="body2" color={colors.redAccent[500]} fontWeight="bold">
            Total Burned
          </Typography>
        </Box>
        <Typography 
          variant="h2" 
          sx={{ 
            fontWeight: "bold",
            color: colors.redAccent[400]
          }}
        >
          {formatNumber(tokenData.burned)}
        </Typography>
      </Card>
    </Box>
  );
};

export default DecTokenStats;